import { useEffect, useRef } from "react";

const STORAGE_KEY = "keywordSearchState";

/**
 * 滚动位置恢复 Hook
 * @param {Object} options
 * @param {React.MutableRefObject} options.stateRef - 保存当前组件状态的 ref
 * @param {Function} options.setStates - 设置恢复状态的回调函数
 * @param {string} [options.storageKey] - 自定义存储 key，默认使用 keywordSearchState
 */
export const useScrollRestoration = (options) => {
  const { stateRef, setStates, storageKey = STORAGE_KEY } = options;
  const hasRestored = useRef(false);
  const savedScrollY = useRef(0);

  // 禁用浏览器自动滚动恢复
  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
  }, []);

  // 捕获离开前的滚动位置
  useEffect(() => {
    const handlePopState = () => {
      savedScrollY.current = window.scrollY;
    };

    const handlePageHide = () => {
      savedScrollY.current = window.scrollY;
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  // 恢复滚动位置（首次渲染时）
  useEffect(() => {
    if (hasRestored.current) return;

    // 检查是否应该跳过恢复（重置按钮触发）
    const skipRestore = sessionStorage.getItem("skipRestore");
    if (skipRestore) {
      sessionStorage.removeItem("skipRestore");
      return;
    }

    const savedState = sessionStorage.getItem(storageKey);
    if (!savedState) return;

    try {
      const parsed = JSON.parse(savedState);
      const now = Date.now();
      // 状态有效期 24 小时
      if (parsed.timestamp && now - parsed.timestamp < 24 * 60 * 60 * 1000) {
        // 优先使用 popstate 捕获的滚动位置
        const scrollY = savedScrollY.current || parsed.scrollY || 0;

        hasRestored.current = true;

        // 关键修复：先恢复状态，等数据渲染完成后再恢复滚动位置
        // 使用 requestAnimationFrame 确保在下一次绘制时执行滚动
        // 直接使用 parsed 作为完整状态对象
        setStates(parsed);

        // 恢复滚动位置 - 使用更精确的时机控制
        const restoreScroll = () => {
          window.scrollTo({
            top: scrollY,
            behavior: "instant",
          });
        };

        // 等待 DOM 更新和渲染完成后再恢复滚动位置
        // 使用 setTimeout 确保在下一个事件循环中执行
        setTimeout(restoreScroll, 0);
        // 如果页面内容较多，可能需要多次尝试
        setTimeout(restoreScroll, 50);
        setTimeout(restoreScroll, 200);
        setTimeout(restoreScroll, 500);
        setTimeout(restoreScroll, 1000);
      }
    } catch (e) {
      console.error("恢复滚动位置失败", e);
    }
  }, [setStates, storageKey]);

  // 保存滚动位置
  useEffect(() => {
    const saveState = () => {
      try {
        const scrollY = savedScrollY.current || window.scrollY;
        const state = stateRef.current;
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            ...state,
            scrollY,
            timestamp: Date.now(),
          }),
        );
      } catch (e) {
        console.error("保存滚动位置失败", e);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        saveState();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      saveState();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [stateRef, storageKey]);
};

export default useScrollRestoration;
